import {ProfileEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
const {next, agreementText, skipStep} = translations['pt-br'].commons;

const FAVORITE_ADDRESS_TUTORIAL_STEPS = {
	PROFILE_MENU: 'profileMenu',
	LIST_WITH_ADDRESSES: 'listWithAddresses',
	LIST_EMPTY: 'listEmpty',
	ADD_IDENTIFICATION: 'addIdentification',
	ADD_SEARCH: 'addSearch',
} as const;

const FAVORITE_ADDRESS_TUTORIAL_STAGES = {
	PROFILE_MENU: 'profileMenu',
	LIST: 'list',
	ADD_IDENTIFICATION: 'addIdentification',
	ADD_SEARCH: 'addSearch',
	COMPLETED: 'completed',
} as const;

const FAVORITE_ADDRESS_TUTORIAL_ACTIONS = {
	COMPLETE: 'complete',
	SKIP: 'skip',
} as const;

const TutorialStepsId = {
	PROFILE_MENU_ADDRESS: 'favorite-address-profile-menu',
	LIST_ADD_BUTTON: 'favorite-address-list-add-button',
	EMPTY_STATE_ADD_BUTTON: 'favorite-address-empty-state-add-button',
	ADDRESS_IDENTIFICATION: 'favorite-address-identification',
	ADDRESS_SEARCH: 'favorite-address-search',
} as const;

type FavoriteAddressTutorialStep =
	(typeof FAVORITE_ADDRESS_TUTORIAL_STEPS)[keyof typeof FAVORITE_ADDRESS_TUTORIAL_STEPS];

type FavoriteAddressTutorialStage =
	(typeof FAVORITE_ADDRESS_TUTORIAL_STAGES)[keyof typeof FAVORITE_ADDRESS_TUTORIAL_STAGES];

type FavoriteAddressTutorialConfig = {
	stage: Exclude<FavoriteAddressTutorialStage, 'completed'>;
	storageKeyPrefix: string;
	targetSelector: string;
	placement: 'bottom' | 'top-end' | 'top-start';
	ctaLabel: string;
	secondaryCtaLabel?: string;
	navigationPath: string | null;
};

type FavoriteAddressTutorialStageProgress = {
	stage: Exclude<FavoriteAddressTutorialStage, 'completed'>;
	steps: readonly FavoriteAddressTutorialStep[];
	completionMode?: 'all' | 'any';
};

const FAVORITE_ADDRESS_TUTORIAL_CONFIG: Record<
	FavoriteAddressTutorialStep,
	FavoriteAddressTutorialConfig
> = {
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.PROFILE_MENU]: {
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.PROFILE_MENU,
		storageKeyPrefix: '@hapvida:favorite-address-profile-menu-tutorial',
		targetSelector: `[data-tag-id="${TutorialStepsId.PROFILE_MENU_ADDRESS}"]`,
		placement: 'bottom',
		ctaLabel: next,
		secondaryCtaLabel: skipStep,
		navigationPath: ProfileEnum.FAVORITE_ADDRESSES_LIST,
	},
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_WITH_ADDRESSES]: {
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.LIST,
		storageKeyPrefix: '@hapvida:favorite-address-list-with-addresses-tutorial',
		targetSelector: `#${TutorialStepsId.LIST_ADD_BUTTON}`,
		placement: 'bottom',
		ctaLabel: next,
		navigationPath: ProfileEnum.FAVORITE_ADDRESSES_ADD,
	},
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_EMPTY]: {
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.LIST,
		storageKeyPrefix: '@hapvida:favorite-address-list-empty-tutorial',
		targetSelector: `#${TutorialStepsId.EMPTY_STATE_ADD_BUTTON}`,
		placement: 'bottom',
		ctaLabel: next,
		navigationPath: ProfileEnum.FAVORITE_ADDRESSES_ADD,
	},
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.ADD_SEARCH]: {
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.ADD_SEARCH,
		storageKeyPrefix: '@hapvida:favorite-address-add-search-tutorial',
		targetSelector: `#${TutorialStepsId.ADDRESS_SEARCH}`,
		placement: 'bottom',
		ctaLabel: next,
		navigationPath: null,
	},
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.ADD_IDENTIFICATION]: {
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.ADD_IDENTIFICATION,
		storageKeyPrefix: '@hapvida:favorite-address-add-identification-tutorial',
		targetSelector: `#${TutorialStepsId.ADDRESS_IDENTIFICATION}`,
		placement: 'top-start',
		ctaLabel: agreementText,
		navigationPath: null,
	},
} as const;

const FAVORITE_ADDRESS_TUTORIAL_STAGE_PROGRESS: readonly FavoriteAddressTutorialStageProgress[] = [
	{
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.PROFILE_MENU,
		steps: [FAVORITE_ADDRESS_TUTORIAL_STEPS.PROFILE_MENU],
	},
	{
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.LIST,
		steps: [
			FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_WITH_ADDRESSES,
			FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_EMPTY,
		],
		completionMode: 'any',
	},
	{
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.ADD_SEARCH,
		steps: [FAVORITE_ADDRESS_TUTORIAL_STEPS.ADD_SEARCH],
	},
	{
		stage: FAVORITE_ADDRESS_TUTORIAL_STAGES.ADD_IDENTIFICATION,
		steps: [FAVORITE_ADDRESS_TUTORIAL_STEPS.ADD_IDENTIFICATION],
	},
] as const;

const getTutorialStepConfig = (step: FavoriteAddressTutorialStep) => {
	return FAVORITE_ADDRESS_TUTORIAL_CONFIG[step];
};

const getStorageKey = (step: FavoriteAddressTutorialStep, loggedUserCode?: string) => {
	const keyParts = [getTutorialStepConfig(step).storageKeyPrefix, loggedUserCode].filter(Boolean);

	return keyParts.join(':');
};

const TUTORIAL_PROGRESS_EVENT = 'favorite-address-tutorial-progress-change';

export {
	FAVORITE_ADDRESS_TUTORIAL_ACTIONS,
	FAVORITE_ADDRESS_TUTORIAL_CONFIG,
	FAVORITE_ADDRESS_TUTORIAL_STAGE_PROGRESS,
	FAVORITE_ADDRESS_TUTORIAL_STAGES,
	FAVORITE_ADDRESS_TUTORIAL_STEPS,
	TUTORIAL_PROGRESS_EVENT,
	TutorialStepsId,
	getStorageKey,
	getTutorialStepConfig,
};
